import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Layers, Zap, Target, RefreshCw, ArrowDown } from 'lucide-react';
interface SlideStrategyProps {
  onContinue: () => void;
}
const PILLARS = [{
  title: 'Leverage existing assets',
  desc: 'Approved materials become interactive Snaks — no new content creation.',
  icon: Layers,
  position: 'top'
}, {
  title: 'Rapid local deployment',
  desc: 'Centrally built, locally activated. Live in days, not months.',
  icon: Zap,
  position: 'right'
}, {
  title: 'Behavioural data capture',
  desc: 'Real HCP engagement signals, not vanity metrics.',
  icon: Target,
  position: 'bottom'
}, {
  title: 'Continuous optimization',
  desc: 'Every interaction sharpens the next campaign — and every market learns.',
  icon: RefreshCw,
  position: 'left'
}] as const;
export function SlideStrategy({
  onContinue
}: SlideStrategyProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [300, 800, 1300, 1800, 2400].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  const placement: Record<string, string> = {
    top: 'md:absolute md:top-0 md:left-1/2 md:-translate-x-1/2 md:w-[300px]',
    right: 'md:absolute md:right-0 md:top-1/2 md:-translate-y-1/2 md:w-[280px]',
    bottom: 'md:absolute md:bottom-0 md:left-1/2 md:-translate-x-1/2 md:w-[300px]',
    left: 'md:absolute md:left-0 md:top-1/2 md:-translate-y-1/2 md:w-[280px]'
  };
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-16 md:py-12 max-w-7xl mx-auto" data-id="element-1064" data-mp="13vumqe78aat">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center space-y-3 shrink-0" data-id="element-1065" data-mp="62z0ue5x0mkd">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-1066" data-mp="20hjg2bbgvpwd">
          The Operating Model
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-white" data-id="element-1067" data-mp="1z0m3dua7lgan">
          A loop, not a project
        </h2>
        <p className="text-lg text-white/60" data-id="element-1068" data-mp="1tz6j44fuvdtb">
          Each campaign cycle feeds the next — across every brand and market.
        </p>
      </motion.div>

      <div className="flex-1 min-h-0 flex items-center justify-center mt-6" data-id="element-1069" data-mp="1k33gyyl07f6d">
        <div className="relative w-full max-w-[900px] md:aspect-[1.35/1] flex flex-col gap-4 md:block" data-id="element-1070" data-mp="2hqc8pbbllkgt">
          {/* Rotating loop */}
          <div className="hidden md:flex absolute inset-0 items-center justify-center pointer-events-none" data-id="element-1071" data-mp="2cqzdo3akp735">
            <svg viewBox="0 0 400 300" className="w-[62%] h-[62%] overflow-visible" aria-hidden="true" data-id="element-1072" data-mp="27zhcbe4n14hn">
              <ellipse cx="200" cy="150" rx="180" ry="130" fill="none" stroke="#e2e8f0" strokeWidth="2" data-id="element-1073" data-mp="30gtoygf8unla" />
              <motion.ellipse cx="200" cy="150" rx="180" ry="130" fill="none" stroke="#7c3aed" strokeWidth="3" strokeLinecap="round" strokeDasharray="120 860" initial={{
              opacity: 0
            }} animate={{
              opacity: stage >= 1 ? 1 : 0,
              strokeDashoffset: [0, -980]
            }} transition={{
              strokeDashoffset: {
                repeat: Infinity,
                duration: 6,
                ease: 'linear'
              },
              opacity: {
                duration: 0.6
              }
            }} data-id="element-1074" data-mp="33zsryvw1ptuy" />
            </svg>
          </div>

          {/* Hub */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: stage >= 5 ? 1 : 0,
          scale: stage >= 5 ? 1 : 0.9
        }} className="md:absolute md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 order-last md:order-none" data-id="element-1075" data-mp="eh0ie7etwskh">
            <div className="bg-teal-400/10 border border-teal-400/30 rounded-2xl px-6 py-5 text-center max-w-[220px] mx-auto" data-id="element-1076" data-mp="1fzx1ttj78xhf">
              <p className="text-teal-400 font-semibold text-xs uppercase tracking-widest" data-id="element-1077" data-mp="s9jbc7g9j6oy">
                Compounding
              </p>
              <p className="text-white font-serif text-xl mt-1 leading-snug" data-id="element-1078" data-mp="nd2jp39uua74">
                Every cycle gets sharper
              </p>
            </div>
          </motion.div>

          {/* Pillars */}
          {PILLARS.map((pillar, idx) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.title} initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: stage >= idx + 1 ? 1 : 0,
            y: stage >= idx + 1 ? 0 : 14
          }} transition={{
            duration: 0.5
          }} className={placement[pillar.position]} data-id="element-1079" data-mp="2cs938w6nbqru" data-mpi={idx}>
                <div className="bg-navy-900 border border-navy-600 rounded-2xl p-5 shadow-[0_8px_30px_rgba(15,23,42,0.06)]" data-id="element-1080" data-mp="2ba1zwb0v7b9i">
                  <div className="flex items-center gap-3 mb-2" data-id="element-1081" data-mp="2yzd4k3lc5psx">
                    <div className="w-9 h-9 rounded-xl bg-teal-400/10 text-teal-400 flex items-center justify-center shrink-0" data-id="element-1082" data-mp="33wmr9bhcbnd2">
                      <Icon className="w-4 h-4" data-id="element-1083" data-mp="2p8i680utezr4" />
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1084" data-mp="2u6tiei8l1mci">
                      <span className="text-teal-400 font-bold text-xs" data-id="element-1085" data-mp="3hxpg1vzpzbro">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <h3 className="text-white font-semibold text-sm md:text-base leading-tight" data-id="element-1086" data-mp="3mpgwhdp5w143">
                        {pillar.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-white/60 text-sm leading-relaxed" data-id="element-1087" data-mp="3riyq7tkrgw9v">
                    {pillar.desc}
                  </p>
                </div>
                {/* Mobile flow arrow */}
                <div className="flex md:hidden justify-center mt-3 text-teal-400/50" data-id="element-1088" data-mp="3ik3igd22je4g">
                  <ArrowDown className="w-4 h-4" data-id="element-1089" data-mp="3dmv0odku1c3q" />
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </div>;
}